import React, { useState, useMemo, useEffect } from 'react';
import { useLocation, useNavigate, Outlet } from 'react-router-dom';
import { useUser } from '../contexts/UserContext';
import { confirmSignOutWithUnsent } from '../config/authRoutes';
import {
  LayoutDashboard, TrendingUp, FolderOpen, Store, DollarSign,
  ShieldAlert, Shield, Users, Megaphone, Handshake, PenLine, FileText,
  Tag, Layers, Home, LogOut, BookOpen, Wallet, Menu, X,
  ChevronDown, ChevronRight, Building2, Settings, FlaskConical, ShieldCheck,
  QrCode, CalendarDays, HeartPulse, Stethoscope, Receipt, CheckSquare, Radar, Contact, MapPin, ServerCog, KeyRound, Briefcase,
  MessageSquare,
  Compass,
  ClipboardCheck,
  Activity,
  Workflow,
} from 'lucide-react';
import { BRAND_ASSETS, TENANT } from '../config';
import { apiRequest } from '../services/api';
import { rememberWorkspace } from '../services/workspacesApi';

/* ── Sandbox trial types ──────────────────────────────────────── */
interface SandboxUsageMeter { used: number; limit: number }
interface SandboxStatus {
  isSandbox: boolean;
  daysLeft?: number;
  expiresAt?: string;
  usage?: {
    esigns: SandboxUsageMeter;
    offerings: SandboxUsageMeter;
    properties: SandboxUsageMeter;
  };
}

/* ── Sandbox usage meter bar ───────────────────────────────────── */
function UsageMeter({ label, used, limit }: { label: string; used: number; limit: number }) {
  const pct = Math.min(100, Math.round((used / limit) * 100));
  const color = pct >= 90 ? 'bg-red-500' : pct >= 70 ? 'bg-amber-400' : 'bg-emerald-500';
  return (
    <div className="flex flex-col gap-0.5 min-w-[80px]">
      <div className="flex justify-between text-[10px] text-amber-800 dark:text-amber-300 font-medium">
        <span>{label}</span>
        <span>{used}/{limit}</span>
      </div>
      <div className="h-1.5 rounded-full bg-amber-200 dark:bg-amber-800">
        <div className={`h-full rounded-full transition-all ${color}`} style={{ width: `${pct}%` }} />
      </div>
    </div>
  );
}

/* ── Sandbox trial banner (shown only in sandbox mode) ─────────── */
function SandboxBanner() {
  const [status, setStatus] = useState<SandboxStatus | null>(null);

  useEffect(() => {
    // Only fetch in sandbox builds — REACT_APP_PAYMENTS_ENV is baked in at build time.
    if (process.env.REACT_APP_PAYMENTS_ENV !== 'sandbox') return;
    apiRequest<SandboxStatus>('GET', '/sandbox/status')
      .then(r => { if (r.success && r.data) setStatus(r.data); })
      .catch(() => {/* not in sandbox or no trial — hide silently */});
  }, []);

  if (!status?.isSandbox || !status.usage) return null;

  return (
    <div className="bg-amber-50 dark:bg-amber-950/30 border-b border-amber-200 dark:border-amber-800 px-3 sm:px-4 py-2 flex flex-wrap items-center gap-2 sm:gap-4">
      <div className="flex items-center gap-1.5 text-xs font-semibold text-amber-900 dark:text-amber-200 shrink-0">
        <FlaskConical className="w-3.5 h-3.5" />
        Sandbox Trial — {status.daysLeft ?? 0} day{status.daysLeft !== 1 ? 's' : ''} remaining
      </div>
      <div className="flex flex-wrap items-center gap-3 sm:gap-4 flex-1 min-w-0">
        <UsageMeter label="E-Signs" used={status.usage.esigns.used} limit={status.usage.esigns.limit} />
        <UsageMeter label="Offerings" used={status.usage.offerings.used} limit={status.usage.offerings.limit} />
        <UsageMeter label="Properties" used={status.usage.properties.used} limit={status.usage.properties.limit} />
      </div>
      <span className="text-[10px] text-amber-700 dark:text-amber-400 shrink-0">
        All data is test-mode only
      </span>
    </div>
  );
}

/* ── Greeting ─────────────────────────────────────────────────── */
function useGreeting(firstName: string) {
  return useMemo(() => {
    const h = new Date().getHours();
    const period = h < 12 ? 'morning' : h < 17 ? 'afternoon' : 'evening';
    return `Good ${period}, ${firstName}`;
  }, [firstName]);
}

/* ── Nav structure ────────────────────────────────────────────── */
interface NavItem { label: string; path: string; icon: React.ReactNode }
interface NavGroup { category: string; items: NavItem[] }

function useNavGroups(): NavGroup[] {
  const { sandboxTenantId } = useUser();
  // When impersonating/logged in as a sandbox tenant admin, suppress platform-only items
  const isSandboxTenant = Boolean(sandboxTenantId);

  return useMemo(() => {
    const groups: NavGroup[] = [
      {
        category: 'Ops',
        items: [
          ...(TENANT.id !== 'emerge' ? [
            { label: 'Capital Raises', path: '/admin/capital-raises',   icon: <TrendingUp className="w-4 h-4" /> },
            { label: 'Projects',       path: '/admin/projects',         icon: <FolderOpen  className="w-4 h-4" /> },
          ] : []),
          ...(TENANT.id === 'emerge' ? [
            { label: 'Operational Command', path: '/admin/command', icon: <Radar className="w-4 h-4" /> },
          ] : []),
          { label: 'Calendar',       path: '/admin/calendar',         icon: <CalendarDays className="w-4 h-4" /> },
          // The sales reps' workspace works off the calendar above it (2026-09-30).
          ...(TENANT.id === 'emerge' ? [
            { label: 'Business Development', path: '/admin/business-development', icon: <Briefcase className="w-4 h-4" /> },
          ] : []),
          ...(!isSandboxTenant ? [
            { label: 'Vendors',        path: '/admin/vendors',          icon: <Store       className="w-4 h-4" /> },
            { label: 'Payroll',        path: '/admin/payroll',          icon: <DollarSign  className="w-4 h-4" /> },
            { label: 'Risk',           path: '/admin/risk',             icon: <ShieldAlert className="w-4 h-4" /> },
            { label: 'AML Compliance', path: '/admin/aml',              icon: <Shield className="w-4 h-4" /> },
          ] : []),
          ...(TENANT.id === 'redex' && !isSandboxTenant ? [{ label: 'Capital Desk', path: '/admin/capital-desk', icon: <Building2 className="w-4 h-4" /> }] : []),
        ],
      },
      {
        category: 'Marketing',
        items: [
          { label: 'Contacts',  path: '/admin/contacts',  icon: <Users     className="w-4 h-4" /> },
          ...(!isSandboxTenant ? [
            { label: 'Campaigns', path: '/admin/campaigns', icon: <Megaphone className="w-4 h-4" /> },
            { label: 'Business Cards', path: '/admin/business-cards', icon: <Contact className="w-4 h-4" /> },
            { label: 'Event Funnels', path: '/admin/event-funnels', icon: <QrCode className="w-4 h-4" /> },
          ] : []),
        ],
      },
      {
        category: 'Finance',
        items: [
          { label: 'Engagements', path: '/admin/engagements', icon: <Handshake  className="w-4 h-4" /> },
          ...(!isSandboxTenant ? [
            { label: 'Transactions', path: '/admin/transactions', icon: <DollarSign className="w-4 h-4" /> },
          ] : []),
          // E-Signatures is the deal-flow signing screen: it lists envelopes in
          // capital-markets terms and its send form takes a single document.
          // eMerge has its own under eMerge Operations > Signatures, which
          // sends packets and designates a counter-signer. Two screens doing
          // the same job differently is how people end up using the wrong one.
          ...(TENANT.id !== 'emerge' ? [
            { label: 'E-Signatures', path: '/admin/esignatures', icon: <PenLine    className="w-4 h-4" /> },
          ] : []),
          // Counter-Sign is a HiveFund page. It reads subscription_agreements,
          // counter-signs page 18 of a subscription document, and displays
          // "Honeyshares Management LLC" and a named fund manager. On eMerge it
          // was in the nav, reachable, and showed another company's name.
          //
          // eMerge counter-signs through the ordinary signing link: a second
          // recipient on the envelope at signing order 2. There is nothing for
          // this page to do here.
          ...(TENANT.id !== 'emerge' ? [
            { label: 'Counter-Sign', path: '/admin/countersign', icon: <ShieldCheck className="w-4 h-4" /> },
          ] : []),
        ],
      },
      {
        category: 'Content',
        items: [
          { label: 'Offerings',                   path: '/admin/offerings',  icon: <Tag       className="w-4 h-4" /> },
          { label: TENANT.branding.propertyLabel, path: '/admin/properties', icon: <Building2 className="w-4 h-4" /> },
          { label: 'Documents',                   path: '/admin/documents',  icon: <FileText  className="w-4 h-4" /> },
          ...(!isSandboxTenant ? [
            { label: 'Page Builder', path: '/admin/page-builder', icon: <Layers className="w-4 h-4" /> },
          ] : []),
        ],
      },
      ...(TENANT.id === 'emerge' ? [{
        category: 'eMerge Operations',
        items: [
          // The workforce workspaces themselves. Each still checks its own
          // capability; an administrator without the role sees its refusal.
          { label: 'Patient Advocate', path: '/portal/patient-advocate', icon: <HeartPulse className="w-4 h-4" /> },
          { label: 'UMED',             path: '/portal/umed',             icon: <ShieldCheck className="w-4 h-4" /> },
          { label: 'Provider',         path: '/provider',                icon: <Stethoscope className="w-4 h-4" /> },
          { label: 'Access Review',    path: '/admin/emerge/access-review',           icon: <KeyRound className="w-4 h-4" /> },
          { label: 'Signatures',       path: '/admin/emerge/signatures',              icon: <PenLine className="w-4 h-4" /> },
          { label: 'Facility Map',     path: '/admin/emerge-map',                     icon: <MapPin className="w-4 h-4" /> },
          { label: 'Coverage',         path: '/admin/emerge/coverage',                icon: <Compass className="w-4 h-4" /> },
          { label: 'Facilities',       path: '/admin/emerge/facilities',              icon: <Building2 className="w-4 h-4" /> },
          { label: 'Patients',         path: '/admin/emerge/patients',                icon: <Users className="w-4 h-4" /> },
          { label: 'Scheduling',       path: '/admin/emerge/scheduling',              icon: <CalendarDays className="w-4 h-4" /> },
          { label: 'Completed Visits', path: '/admin/emerge/completed_visits',        icon: <CheckSquare className="w-4 h-4" /> },
          { label: 'Hearing Aids',     path: '/admin/emerge/hearing_aids',            icon: <Stethoscope className="w-4 h-4" /> },
          { label: 'Qualification',    path: '/admin/emerge/patient_qualification',   icon: <ShieldCheck className="w-4 h-4" /> },
          { label: 'Advocate Leads',   path: '/admin/emerge/patient_advocate_leads',  icon: <HeartPulse className="w-4 h-4" /> },
          { label: 'Finance Accounts', path: '/admin/emerge/finance_accounts',        icon: <DollarSign className="w-4 h-4" /> },
          { label: 'Invoices',         path: '/admin/emerge/invoices',                icon: <Receipt className="w-4 h-4" /> },
          { label: 'Payments',         path: '/admin/emerge/payments',                icon: <Wallet className="w-4 h-4" /> },
          { label: 'Feedback',         path: '/admin/emerge/feedback',                icon: <MessageSquare className="w-4 h-4" /> },
          { label: 'Workflow Health',  path: '/admin/emerge/workflow-health',         icon: <Activity className="w-4 h-4" /> },
          { label: 'Record Flow',      path: '/admin/record-flow',                    icon: <Workflow className="w-4 h-4" /> },
          { label: 'Account Recovery', path: '/admin/emerge/account-recovery',        icon: <KeyRound className="w-4 h-4" /> },
        ],
      }] : []),
      // Sandbox wizard — only rendered when REACT_APP_PAYMENTS_ENV=sandbox is baked
      // into the build. Production builds never set this variable so this block is
      // compiled away and the nav item never appears in prod.
      ...(process.env.REACT_APP_PAYMENTS_ENV === 'sandbox' ? [{
        category: 'Sandbox',
        items: [
          { label: 'Trial Setup', path: '/admin/sandbox/setup',    icon: <FlaskConical className="w-4 h-4" /> },
          { label: 'Tenants',     path: '/admin/sandbox/tenants',  icon: <Users className="w-4 h-4" /> },
        ],
      }] : []),
    ];
    return groups;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSandboxTenant]);
}

/* ── Sidebar ──────────────────────────────────────────────────── */
function Sidebar({
  groups,
  isActive,
  onNavigate,
  onLogout,
  collapsed,
  isDesktop,
  isSandboxTenantNav,
}: {
  groups: NavGroup[];
  isActive: (p: string) => boolean;
  onNavigate: (p: string) => void;
  onLogout: () => void;
  collapsed: boolean;
  isDesktop: boolean;
  isSandboxTenantNav: boolean;
}) {
  const [open, setOpen] = useState<Record<string, boolean>>(() =>
    Object.fromEntries(groups.map(g => [g.category, true]))
  );
  const toggle = (cat: string) => setOpen(prev => ({ ...prev, [cat]: !prev[cat] }));

  return (
    <aside
      className={`fixed top-0 left-0 h-screen bg-[#F0F0F0] border-r border-border flex flex-col z-50 transition-all duration-200 ${
        isDesktop
          ? (collapsed ? 'w-0 overflow-hidden' : 'w-56')
          : `w-[86vw] max-w-xs shadow-2xl ${collapsed ? '-translate-x-full' : 'translate-x-0'}`
      }`}
      aria-hidden={!isDesktop && collapsed}
    >
      {/* Logo */}
      <div
        className="flex items-center h-16 px-4 border-b border-border cursor-pointer shrink-0"
        onClick={() => onNavigate('/admin/dashboard')}
      >
        <img src={BRAND_ASSETS.logo.primary} alt="Logo" className="h-8 w-auto object-contain" />
      </div>

      {/* Dashboard shortcut */}
      <div className="px-3 pt-3 pb-1">
        <button
          onClick={() => onNavigate('/admin/dashboard')}
          className={`w-full flex items-center gap-2.5 px-3 py-2 rounded-md text-sm font-medium transition-colors ${
            isActive('/admin/dashboard')
              ? 'bg-black/10 text-foreground'
              : 'text-muted-foreground hover:text-foreground hover:bg-black/5'
          }`}
        >
          <LayoutDashboard className="w-4 h-4 shrink-0" />
          Dashboard
        </button>
      </div>

      {/* Grouped nav */}
      <nav className="flex-1 overflow-y-auto px-3 py-2 space-y-1">
        {groups.map(group => (
          <div key={group.category}>
            {/* Category header */}
            <button
              onClick={() => toggle(group.category)}
              className="w-full flex items-center justify-between px-2 py-1.5 text-[10px] font-bold tracking-widest uppercase text-muted-foreground/60 hover:text-muted-foreground transition-colors"
            >
              {group.category}
              {open[group.category]
                ? <ChevronDown className="w-3 h-3" />
                : <ChevronRight className="w-3 h-3" />
              }
            </button>

            {/* Items */}
            {open[group.category] && (
              <div className="space-y-0.5 mb-2">
                {group.items.map(item => (
                  <button
                    key={item.path}
                    onClick={() => onNavigate(item.path)}
                    className={`w-full flex items-center gap-2.5 px-3 py-2 rounded-md text-sm transition-colors ${
                      isActive(item.path)
                        ? 'bg-black/10 text-foreground font-medium'
                        : 'text-muted-foreground hover:text-foreground hover:bg-black/5'
                    }`}
                  >
                    <span className="shrink-0">{item.icon}</span>
                    <span className="truncate">{item.label}</span>
                  </button>
                ))}
              </div>
            )}
          </div>
        ))}
      </nav>

      {/* Bottom actions */}
      <div className="border-t border-border px-3 py-3 space-y-0.5 shrink-0">
        {!isSandboxTenantNav && (
          <button
            onClick={() => onNavigate('/admin/wallet')}
            className="w-full flex items-center gap-2.5 px-3 py-2 rounded-md text-sm text-muted-foreground hover:text-foreground hover:bg-black/5 transition-colors"
          >
            <Wallet className="w-4 h-4 shrink-0" /> Treasury
          </button>
        )}
        {/* The provider portal, for admins who also hold a ratified
            PRACTITIONER assignment. It lives outside /admin because it is
            the providers' own surface; this is a door to it, not a copy. */}
        {TENANT.id === 'emerge' && (
          <button
            onClick={() => onNavigate('/provider')}
            className="w-full flex items-center gap-2.5 px-3 py-2 rounded-md text-sm text-muted-foreground hover:text-foreground hover:bg-black/5 transition-colors"
          >
            <Stethoscope className="w-4 h-4 shrink-0" /> Provider Portal
          </button>
        )}
        {/* The advocate workspace (EP-004C). Same reasoning: the advocates'
            own surface, read-only, gated server-side by ADVOCACY_LEAD_VIEW. */}
        {TENANT.id === 'emerge' && (
          <button
            onClick={() => onNavigate('/portal/patient-advocate')}
            className="w-full flex items-center gap-2.5 px-3 py-2 rounded-md text-sm text-muted-foreground hover:text-foreground hover:bg-black/5 transition-colors"
          >
            <HeartPulse className="w-4 h-4 shrink-0" /> Advocate Portal
          </button>
        )}
        {/* The UMED operations workspace (EP-004U phase 2), directly under the
            advocate portal because that is the order the work happens in: an
            advocate enrols the resident, then a UMED is created, submitted and
            chased through DCF approval.

            Read-only, and gated server-side by UMED_CASE_VIEW plus the eMerge
            PHI operator list. Like the two doors above it, this is a link and
            not a permission — an admin without a ratified UMED role sees the
            button and is refused by the server, which tells them which of the
            two gates they are missing. */}
        {TENANT.id === 'emerge' && (
          <button
            onClick={() => onNavigate('/portal/umed')}
            className="w-full flex items-center gap-2.5 px-3 py-2 rounded-md text-sm text-muted-foreground hover:text-foreground hover:bg-black/5 transition-colors"
          >
            <ClipboardCheck className="w-4 h-4 shrink-0" /> UMED Operations
          </button>
        )}
        <button
          onClick={() => onNavigate('/admin/system')}
          className="w-full flex items-center gap-2.5 px-3 py-2 rounded-md text-sm text-muted-foreground hover:text-foreground hover:bg-black/5 transition-colors"
        >
          <ServerCog className="w-4 h-4 shrink-0" /> System Admin
        </button>
        <button
          onClick={() => onNavigate('/admin/users')}
          className="w-full flex items-center gap-2.5 px-3 py-2 rounded-md text-sm text-muted-foreground hover:text-foreground hover:bg-black/5 transition-colors"
        >
          <Settings className="w-4 h-4 shrink-0" /> Users
        </button>
        <button
          onClick={() => onNavigate('/admin/access-requests')}
          className="w-full flex items-center gap-2.5 px-3 py-2 rounded-md text-sm text-muted-foreground hover:text-foreground hover:bg-black/5 transition-colors"
        >
          <ShieldAlert className="w-4 h-4 shrink-0" /> Access Requests
        </button>
        {/* Investor surface, and it links to /portal/invest/properties. eMerge
            has no investment product, so the destination is meaningless there —
            unlike the nav array above, this button was never tenant-gated. */}
        {TENANT.id !== 'emerge' && (
          <button
            onClick={() => onNavigate('/portal/invest/properties')}
            className="w-full flex items-center gap-2.5 px-3 py-2 rounded-md text-sm text-muted-foreground hover:text-foreground hover:bg-black/5 transition-colors"
          >
            <Home className="w-4 h-4 shrink-0" /> Investor Portal
          </button>
        )}
        <button
          onClick={onLogout}
          className="w-full flex items-center gap-2.5 px-3 py-2 rounded-md text-sm text-destructive hover:text-destructive/80 hover:bg-black/5 transition-colors"
        >
          <LogOut className="w-4 h-4 shrink-0" /> Logout
        </button>
      </div>
    </aside>
  );
}

/* ── Main layout ──────────────────────────────────────────────── */
const AdminLayout: React.FC = () => {
  const { user, logout, sandboxTenantId } = useUser();
  const location = useLocation();
  const navigate = useNavigate();
  const [isDesktop, setIsDesktop] = useState(() =>
    typeof window === 'undefined' ? true : window.matchMedia('(min-width: 1024px)').matches
  );
  const [sidebarOpen, setSidebarOpen] = useState(() =>
    typeof window === 'undefined' ? true : window.matchMedia('(min-width: 1024px)').matches
  );
  const [sandboxPayments, setSandboxPayments] = useState(() =>
    localStorage.getItem('sandboxPayments') === 'true'
  );

  const isSandboxTenant = Boolean(sandboxTenantId);
  const firstName = user?.email?.split('@')[0] || 'Admin';
  const greeting = useGreeting(firstName);
  const groups = useNavGroups();

  const isActive = (path: string) =>
    location.pathname === path || location.pathname.startsWith(path + '/');

  // EP-005: the console is remembered as the last workspace used, so an
  // administrator who works here lands here; one who last worked in a
  // workforce workspace lands there instead.
  useEffect(() => {
    if (TENANT.id === 'emerge') rememberWorkspace('ADMIN');
  }, []);

  useEffect(() => {
    const media = window.matchMedia('(min-width: 1024px)');
    const handleChange = (event: MediaQueryListEvent | MediaQueryList) => {
      setIsDesktop(event.matches);
      setSidebarOpen(event.matches);
    };

    handleChange(media);
    media.addEventListener('change', handleChange);
    return () => media.removeEventListener('change', handleChange);
  }, []);

  useEffect(() => {
    if (!isDesktop) setSidebarOpen(false);
  }, [location.pathname, isDesktop]);

  const handleNavigate = (path: string) => {
    navigate(path);
    if (!isDesktop) setSidebarOpen(false);
  };

  const handleLogout = async () => {
    if (!confirmSignOutWithUnsent(user?.id)) return; // unsent notes on this computer: ask first
    await logout();
    navigate('/');
  };

  const toggleSandboxPayments = () => {
    const next = !sandboxPayments;
    setSandboxPayments(next);
    localStorage.setItem('sandboxPayments', String(next));
  };

  return (
    <div className="min-h-screen bg-background portal-typography flex overflow-x-hidden">
      {!isDesktop && sidebarOpen && (
        <button
          type="button"
          aria-label="Close admin navigation"
          className="fixed inset-0 z-40 bg-black/40 backdrop-blur-[1px] lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <Sidebar
        groups={groups}
        isActive={isActive}
        onNavigate={handleNavigate}
        onLogout={handleLogout}
        collapsed={!sidebarOpen}
        isDesktop={isDesktop}
        isSandboxTenantNav={isSandboxTenant}
      />

      {/* Content area */}
      <div
        className={`flex-1 flex flex-col min-h-screen min-w-0 w-full transition-all duration-200 ${
          isDesktop && sidebarOpen ? 'lg:ml-56' : 'ml-0'
        }`}
      >
        {/* Top header */}
        <header className="sticky top-0 z-40 bg-[#F0F0F0] border-b border-border flex items-center justify-between gap-2 px-3 sm:px-4 h-14 sm:h-16 shrink-0">
          <div className="flex items-center gap-2 sm:gap-3 min-w-0">
            <button
              onClick={() => setSidebarOpen(v => !v)}
              className="p-1.5 rounded-md text-muted-foreground hover:text-foreground hover:bg-black/5 transition-colors"
              aria-label="Toggle sidebar"
            >
              {sidebarOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
            </button>
            <h1 className="text-sm font-medium text-foreground truncate">{greeting}</h1>
          </div>

          <div className="flex items-center gap-1.5 sm:gap-2 shrink-0">
            <span className="hidden sm:inline-flex text-xs text-muted-foreground px-2 py-1 rounded-full bg-[hsl(var(--chart-2))]/10 text-[hsl(var(--chart-2))] font-semibold">
              Admin
            </span>
            {/* Sandbox payments toggle — admin-only, runtime switch */}
            {user?.role === 'admin' && (
              <button
                onClick={toggleSandboxPayments}
                className={`text-xs px-2 sm:px-2.5 py-1 rounded-full font-semibold flex items-center gap-1 transition-colors ${
                  sandboxPayments
                    ? 'bg-amber-100 text-amber-800 ring-1 ring-amber-300'
                    : 'bg-muted text-muted-foreground hover:bg-muted/80'
                }`}
                title={sandboxPayments ? 'Sandbox payments ON — Plaid/Increase use test credentials' : 'Click to enable sandbox payment mode'}
              >
                <FlaskConical className="w-3 h-3" />
                <span className="hidden sm:inline">{sandboxPayments ? 'Sandbox ON' : 'Sandbox'}</span>
              </button>
            )}
            {process.env.REACT_APP_PAYMENTS_ENV === 'sandbox' && (
              <span className="text-xs px-2 py-1 rounded-full bg-amber-100 text-amber-800 font-semibold flex items-center gap-1">
                <FlaskConical className="w-3 h-3" /> Sandbox
              </span>
            )}
            <button
              onClick={() => navigate('/admin/documentation')}
              className="p-1.5 rounded-md text-muted-foreground hover:text-foreground hover:bg-black/5 transition-colors"
              aria-label="Documentation"
            >
              <BookOpen className="w-4 h-4" />
            </button>
          </div>
        </header>

        {/* Sandbox trial banner — visible only when PAYMENTS_ENV=sandbox */}
        <SandboxBanner />

        {/* Sandbox payments mode banner */}
        {sandboxPayments && (
          <div className="bg-amber-50 dark:bg-amber-950/30 border-b border-amber-200 dark:border-amber-800 px-3 sm:px-4 py-1.5 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-1.5 sm:gap-3">
            <div className="flex items-start sm:items-center gap-2 text-xs font-medium text-amber-800 dark:text-amber-200 min-w-0">
              <FlaskConical className="w-3.5 h-3.5 shrink-0 mt-0.5 sm:mt-0" />
              Sandbox Payment Mode — Plaid &amp; Increase using test credentials. Transactions are not real.
            </div>
            <button
              onClick={toggleSandboxPayments}
              className="text-left sm:text-right text-xs text-amber-700 dark:text-amber-300 hover:text-amber-900 font-semibold underline shrink-0"
            >
              Disable
            </button>
          </div>
        )}

        {/* Page content */}
        <main className="admin-content flex-1 min-w-0 w-full max-w-7xl mx-auto px-3 py-4 sm:px-5 sm:py-5 lg:px-6 lg:py-6 overflow-x-hidden">
          <Outlet />
        </main>
      </div>
    </div>
  );
};

export default AdminLayout;
